import type React from "react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { SelectWrapper } from "../../app/SelectWrapper";
import { FieldMessages, fieldAccessibility, type LegacyField } from "./LegacyFieldAdapter";

// Redux Form adapter for async multiple choice, extracted from inputs.jsx as
// part of removing react-filtered-multiselect. inputs.jsx re-exports this as
// `AsyncMultiSelectField`, so the four existing call sites are unchanged.
//
// The dual-pane filtered list is gone from this path: ordinary async multiple
// choice is a multi-value combobox, which SelectWrapper already owns. The
// genuine opt-out/opt-in workflow lives in app/PreferenceTransferList.
//
// The adapter owns every Redux Form concern — reading field.input, translating
// the selection back into field.input.onChange, rendering the label and the
// messages. See docs/multiselect-migration-audit.md.

type OptionValue = number | string;

type MultiSelectOption = {
  label?: string;
  value: OptionValue;
};

/**
 * The legacy loader contract: called with no argument and resolving either an
 * array of options or the `{ options }` envelope the actions layer produces.
 */
type LoadOptions = () => Promise<MultiSelectOption[] | { options: MultiSelectOption[] } | null | undefined>;

/** The imperative surface the two user forms drive through `refName`. */
type MultiSelectFieldHandle = {
  reload: () => void;
};

type MultiSelectFieldProps = LegacyField & {
  className?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  id?: string;
  input: {
    name?: string;
    onBlur?: (value?: OptionValue[] | string | null) => void;
    onChange: (value: OptionValue[]) => void;
    value?: OptionValue[] | string | null;
  };
  loadOptions: LoadOptions;
  /** Legacy value-changed callback; receives the selected option objects. */
  onInputChange?: (options: MultiSelectOption[]) => void;
  placeholder?: string;
  refName?: (handle: MultiSelectFieldHandle | null) => void;
  /** Overrides the Redux Form value for display. */
  selectedOptions?: OptionValue[];
};

function toOptions(result: Awaited<ReturnType<LoadOptions>>): MultiSelectOption[] {
  if (Array.isArray(result)) {
    return result;
  }
  return Array.isArray(result?.options) ? result.options : [];
}

/** The props this adapter sends into the react-select boundary. */
type OwnedMultiSelectProps = {
  "aria-describedby": string | undefined;
  "aria-invalid": true | undefined;
  "aria-labelledby": string | undefined;
  "aria-required": true | undefined;
  className: string | undefined;
  disabled: boolean | undefined;
  id: string;
  isLoading: boolean;
  multi: true;
  name: string | undefined;
  onBlur: (() => void) | undefined;
  onChange: (selected: MultiSelectOption[] | null) => void;
  options: MultiSelectOption[];
  placeholder: string | undefined;
  styles: {
    valueContainer: (base: Record<string, unknown>) => Record<string, unknown>;
  };
  value: OptionValue[];
};

const boundedSelectionStyles = {
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    alignContent: "flex-start",
    maxHeight: "7rem",
    overflowY: "auto",
  }),
};

// SelectWrapper is untyped JSX — it is react-select's sole owner and predates
// the TypeScript boundary — so what this adapter sends is declared here rather
// than inferred. The plan already records that a typed module consumed from, or
// consuming, untyped JSX gives near-zero checking at that seam; this at least
// pins the adapter's own half of the contract.
const MultiValueSelect = SelectWrapper as unknown as React.ComponentType<OwnedMultiSelectProps>;

export default function MultiSelectField(field: MultiSelectFieldProps): React.ReactElement {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const labelId = `${inputId}-label`;
  const accessibility = fieldAccessibility(field, inputId);

  const [options, setOptions] = useState<MultiSelectOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Call sites build `loadOptions` inline, so its identity changes on every
  // render. The replaced control read `this.props.loadOptions` at call time and
  // fetched only from componentDidMount or an explicit reload; holding the
  // newest function in a ref reproduces that without an unbounded fetch loop.
  const latestLoadOptions = useRef(field.loadOptions);
  useEffect(() => {
    latestLoadOptions.current = field.loadOptions;
  }, [field.loadOptions]);

  const mounted = useRef(false);
  const load = useCallback(() => {
    setLoading(true);
    // Invoked with no argument, exactly as the replaced control did, so the
    // request carries no search term and the full set is returned once.
    // Narrowing then happens client-side inside the combobox.
    return latestLoadOptions
      .current()
      .then((result) => {
        if (mounted.current) {
          setOptions(toOptions(result));
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted.current) {
          setLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  const handle = useMemo<MultiSelectFieldHandle>(() => ({ reload: load }), [load]);
  const assignHandle = field.refName;
  useEffect(() => {
    if (!assignHandle) {
      return undefined;
    }
    assignHandle(handle);
    return () => assignHandle(null);
  }, [assignHandle, handle]);

  // `field.selectedOptions` may legitimately be an empty array, which is
  // truthy, so `||` is the operator the previous implementation relied on and
  // is kept. The previous code wrapped a string value with `split()` — with no
  // separator that yields exactly `[value]`, including for the empty string, so
  // the array literal is the same value written so TypeScript can see it.
  const value =
    field.selectedOptions || (typeof field.input.value === "string" ? [field.input.value] : field.input.value) || [];

  const handleChange = (selected: MultiSelectOption[] | null) => {
    const selection = selected || [];
    const selectedByValue = new Map(selection.map((option) => [String(option.value), option]));
    const ordered = options
      .filter((option) => selectedByValue.has(String(option.value)))
      .map((option) => selectedByValue.get(String(option.value)) as MultiSelectOption);
    const knownValues = new Set(ordered.map((option) => String(option.value)));
    ordered.push(...selection.filter((option) => !knownValues.has(String(option.value))));
    field.input.onChange(ordered.map((option) => option.value));
    field.onInputChange?.(ordered);
  };

  return (
    <div className={accessibility.hasError ? "app-validation-error" : undefined}>
      {!field.hideLabel && (
        <label className="app-form-label" htmlFor={inputId} id={labelId}>
          {field.placeholder}
        </label>
      )}
      <MultiValueSelect
        aria-describedby={accessibility.inputProps["aria-describedby"]}
        aria-invalid={accessibility.inputProps["aria-invalid"]}
        aria-labelledby={field.hideLabel ? undefined : labelId}
        aria-required={accessibility.inputProps["aria-required"]}
        className={field.className}
        disabled={field.disabled}
        id={inputId}
        isLoading={loading}
        multi
        name={field.input.name}
        // Selecting an option can blur before React Redux commits the new
        // array. A zero-payload blur marks touched without restoring the array
        // captured by the previous render.
        onBlur={field.input.onBlur ? () => field.input.onBlur?.() : undefined}
        onChange={handleChange}
        options={options}
        placeholder={field.placeholder}
        styles={boundedSelectionStyles}
        value={value}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}
